import { TenantListItem, TenantAssignmentParams, TenantActivationParams, CreateTenant, UpdateTenant } from "../../models/users";
import { api } from "./api";
import { mapTenant } from "../../utils/mappers";

type ApiRecord = Record<string, unknown>;
type TenantListApiResponse =
  | ApiRecord[]
  | {
      success?: boolean;
      data?: ApiRecord[] | { items?: ApiRecord[] };
      items?: ApiRecord[];
    };

type TenantEntityApiResponse =
  | ApiRecord
  | {
      success?: boolean;
      data?: ApiRecord;
    };

const isApiRecord = (value: unknown): value is ApiRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isApiRecordList = (value: unknown): value is ApiRecord[] =>
  Array.isArray(value) && value.every(isApiRecord);

const getTenantsPayload = (response: TenantListApiResponse): ApiRecord[] => {
  const payload = Array.isArray(response) ? response : response.data;

  if (isApiRecordList(payload)) return payload;
  if (isApiRecord(payload) && isApiRecordList(payload.items)) return payload.items;
  if (!Array.isArray(response) && isApiRecordList(response.items)) return response.items;

  return [];
};

const getTenantPayload = (response: TenantEntityApiResponse): ApiRecord => {
  if ("data" in response && isApiRecord(response.data)) return response.data;
  return response;
};

const stringValue = (value: unknown): string => (typeof value === "string" ? value : "");

const booleanValue = (value: unknown): boolean => {
  if (typeof value === "boolean") return value;
  if (value === 1 || value === "1" || value === "true") return true;
  return false;
};

export type TenantsQueryParams = {
  provider?: string;
  theme?: string;
  limit?: string;
};

/** Omite filtros de texto vacíos para evitar requests inválidas. */
export const normalizeTenantsQueryArgs = (
  params: TenantsQueryParams = {}
): TenantsQueryParams => {
  const normalized: TenantsQueryParams = {};

  if (params.provider?.trim()) normalized.provider = params.provider.trim();
  if (params.theme?.trim()) normalized.theme = params.theme.trim();
  if (params.limit != null) normalized.limit = params.limit;

  return normalized;
};

const mapTenantDetail = (payload: ApiRecord): UpdateTenant => ({
  id: stringValue(payload.id),
  provider: stringValue(payload.provider),
  theme: stringValue(payload.theme),
  logo: stringValue(payload.logo),
  alternateLogo: stringValue(payload.alternateLogo),
  profileLogo: stringValue(payload.profileLogo),
  emailDomain: stringValue(payload.emailDomain),
  showMainBanner: booleanValue(payload.showMainBanner),
  defaultShowEventMap: booleanValue(payload.defaultShowEventMap),
});

export const tenantsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getTenants: build.query<TenantListItem[], TenantsQueryParams>({
      query: (params) => ({
        url: "/bo/tenants",
        params: normalizeTenantsQueryArgs(params),
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const normalized = normalizeTenantsQueryArgs(queryArgs || {});
        return `${endpointName}(${JSON.stringify(normalized)})`;
      },
      transformResponse: (response: TenantListApiResponse) => {
        const tenants = getTenantsPayload(response);
        return tenants.map(mapTenant);
      },
      providesTags: ["tenants"],
    }),
    getTenantById: build.query<UpdateTenant, string>({
      query: (id) => ({
        url: `/bo/tenants/${id}`,
      }),
      providesTags: (result, error, id) => [{ type: "tenants", id }],
      transformResponse: (response: TenantEntityApiResponse) => {
        return mapTenantDetail(getTenantPayload(response));
      },
    }),
    createTenant: build.mutation<void, CreateTenant>({
      query: (data) => ({
        url: "/bo/tenants",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["tenants"],
    }),
    updateTenant: build.mutation<void, UpdateTenant>({
      query: (data) => ({
        url: "/bo/tenants",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: "tenants", id }],
    }),
    deleteTenant: build.mutation<void, string>({
      query: (id) => ({
        url: `/bo/tenants/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [{ type: "tenants", id }],
    }),
    assignUserToTenant: build.mutation<void, TenantAssignmentParams>({
      query: ({ tenantId, userId }) => ({
        url: `/bo/tenants/${tenantId}/users/bulk`,
        method: "POST",
        body: { userIds: [userId] },
      }),
    }),
    bulkAssignUsersToTenant: build.mutation<unknown, { tenantId: string; userIds: number[] }>({
      query: ({ tenantId, userIds }) => ({
        url: `/bo/tenants/${tenantId}/users/bulk`,
        method: "POST",
        body: { userIds },
      }),
    }),
    removeUserFromTenant: build.mutation<void, TenantAssignmentParams>({
      query: ({ tenantId, userId }) => ({
        url: `/bo/tenants/${tenantId}/users/${userId}`,
        method: "DELETE",
      }),
    }),
    updateUserTenantStatus: build.mutation<void, TenantActivationParams>({
      query: ({ userId, tenantId, active }) => ({
        url: `/bo/tenants/${tenantId}/users/${userId}/status`,
        method: "PATCH",
        body: { active },
      }),
    }),
  }),
});

export const {
  useGetTenantsQuery,
  useGetTenantByIdQuery,
  useCreateTenantMutation,
  useUpdateTenantMutation,
  useDeleteTenantMutation,
  useAssignUserToTenantMutation,
  useBulkAssignUsersToTenantMutation,
  useRemoveUserFromTenantMutation,
  useUpdateUserTenantStatusMutation
} = tenantsApi;
